'use client';

/**
 * Shared presentational primitives for the trial-activation gate (LF-4076).
 *
 * Promoted from the LF-3933 Storybook prototypes so production and the
 * prototype variants render from one source. Purely presentational — the
 * real Stripe round-trip is driven by the gate / landing, not here.
 */

import { signOut } from 'next-auth/react';
import { ArrowRight, CheckCircle2, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * idle      → waiting for the user to click "Activate Trial"
 * redirecting → creating the Stripe session / navigating to checkout
 * activated → returned from Stripe; trial confirmed, handing to the app
 */
export type ActivationState = 'idle' | 'redirecting' | 'activated';

interface ActivateTrialButtonProps {
  state: ActivationState;
  onActivate: () => void;
  label?: string;
  className?: string;
}

/** The fundamental action — identical wording and behaviour everywhere */
export function ActivateTrialButton({
  state,
  onActivate,
  label = 'Activate Trial',
  className
}: ActivateTrialButtonProps) {
  return (
    <Button
      size="xs"
      density="compact"
      gap="standard"
      className={cn('h-11 gap-2 text-sm', className)}
      onClick={onActivate}
      disabled={state !== 'idle'}
    >
      {state === 'redirecting' ? (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Opening secure checkout…
        </>
      ) : (
        <>
          {label}
          <ArrowRight className="h-3.5 w-3.5" />
        </>
      )}
    </Button>
  );
}

/** Trust microcopy that always travels with the CTA */
export function StripeReassurance({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'text-muted-foreground flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs',
        className
      )}
    >
      <span className="flex items-center gap-1">
        <Lock className="h-3 w-3" />
        Secure checkout by Stripe
      </span>
      <span aria-hidden="true">·</span>
      <span>No charge today</span>
      <span aria-hidden="true">·</span>
      <span>Cancel anytime</span>
    </div>
  );
}

/**
 * The return from Stripe — the firm has just converted and is about to be
 * handed to the app. Shown briefly while the gate polls for the status to flip.
 *
 * The headline and body are deliberately trial-neutral, true whether or not the
 * firm actually has a free trial (VALE-110): a `pending` firm converts to
 * `membershipStatus: 'trial'` even when it's a future-paid-start firm with
 * `hasFreeTrial === false` (`customTrialDays = 0`, a pre-set future
 * `trialExpiresAt`), so this screen cannot reliably tell the two apart — the
 * activation summary that carries `hasFreeTrial` is deliberately not fetched on
 * the return path. The real trial dates are conveyed correctly on the billing
 * tab and the activation landing, both gated on `hasFreeTrial` since LF-6355.
 *
 * The sub-line used to branch on a `variant` prop (LF-4535), whose only other
 * value served the `active-free` opt-in path. That path was retired with the
 * `trial-gate-opt-in` flag in VALE-1540, leaving `pending` as the sole firm
 * shape that can reach this screen — so the sub-line is now unconditional
 * rather than a choice with one live arm.
 */
export function ActivatedPanel({ firmName }: { firmName: string }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="bg-success-subtle flex h-12 w-12 items-center justify-center rounded-full">
        <CheckCircle2 className="text-success-subtle-foreground h-6 w-6" />
      </div>
      <h2 className="text-foreground mt-4 text-xl font-semibold tracking-tight">
        Your plan is active
      </h2>
      <p className="text-muted-foreground mt-2 max-w-xs text-sm leading-relaxed">
        Welcome to Vale, {firmName}. Your whole team now has access.
      </p>
      <p className="text-muted-foreground mt-6 flex items-center gap-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Taking you to your firm setup…
      </p>
    </div>
  );
}

/** A blocked page must always offer an escape hatch */
export function SignedInFooter({
  email,
  className
}: {
  email: string;
  className?: string;
}) {
  return (
    <p className={cn('text-muted-foreground text-xs', className)}>
      Signed in as {email} ·{' '}
      <button
        type="button"
        className="hover:text-foreground-secondary underline underline-offset-2"
        onClick={() => signOut({ callbackUrl: '/login' })}
      >
        Sign out
      </button>
    </p>
  );
}
